import React, { useEffect, useRef, useState } from "react";
import RealtimeQueues from "./RealtimeQueues";
import RealtimeAgents from "./RealtimeAgents";
import RealtimeQueuesGeneral from "./RealtimeQueuesGeneral";
import { MdFullscreen, MdOutlineSettings } from "react-icons/md";
import { FaSortAlphaDown, FaUser } from "react-icons/fa";
import { FiPhoneIncoming } from "react-icons/fi";
import { EuiLoadingLogo } from "@elastic/eui";
import RealtimeService from "../../services/v2/realtimev2Service";
import PermissionsKo from "../shared/PermissionsKo";
import { useTheme } from "../../context/ThemeContext";

const RealtimeMain = () => {
  const [permissions, setPermissions] = useState({
    realtimeQueuePermissions: false,
    realtimeAgentPermissions: false,
    realtimeAgents: [],
    realtimeQueues: [],
  });

  const [generalStats, setGeneralStats] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [agentSortOption, setAgentSortOption] = useState("alphabetical");
  const [queueSortOption, setQueueSortOption] = useState("alphabetical");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const contentRef = useRef(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const { isDarkMode } = useTheme();

  const handleFullScreen = () => {
    if (contentRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        contentRef.current.requestFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const {
          data: { data },
        } = await RealtimeService.getPermissions();
        setPermissions({
          realtimeQueuePermissions: data.queues && data.queues.length > 0,
          realtimeAgentPermissions: data.users && data.users.length > 0,
          realtimeAgents: data.users || [],
          realtimeQueues: data.queues || [],
        });
        setGeneralStats(data.generalStats);
      } catch (error) {
        console.log(error);
        setIsUnauthorized(true);
        setPermissions({
          realtimeQueuePermissions: false,
          realtimeAgentPermissions: false,
          realtimeAgents: [],
          realtimeQueues: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    if (
      !permissions.realtimeQueuePermissions &&
      !permissions.realtimeAgentPermissions
    ) {
      return;
    }

    const queueIds = permissions.realtimeQueues;
    const agentIds = permissions.realtimeAgents.map(
      (agent) => agent.userIdentity
    );

    const fetchRealtimeStatus = async () => {
      try {
        const {
          data: { data },
        } = await RealtimeService.realtimeStatus({
          queues: queueIds,
          agents: agentIds,
        });

        setPermissions((prev) => ({
          ...prev,
          // Actualización de agentes (sin cambios)
          realtimeAgents: prev.realtimeAgents.map((agent) => {
            const updatedAgent = data.realtimeAgents.find(
              (a) => a.id === agent.userIdentity
            );
            if (updatedAgent) {
              return {
                ...agent,
                presence: updatedAgent?.presence?.toLowerCase(),
                name: updatedAgent?.name || agent?.name,
                presenceActivity: updatedAgent.presenceActivity,
                statusStartTime: updatedAgent.statusStartTime,
                in: updatedAgent.in,
                out: updatedAgent.out,
                queue: updatedAgent.queue,
                number: updatedAgent.number,
                id: updatedAgent.id,
              };
            }
            return agent;
          }),
          // Actualización de colas
          realtimeQueues: queueIds.map((queueId) => {
            const updatedQueue = data.realtimeQueues.find(
              (q) => q.id === queueId
            );
            return (
              updatedQueue || {
                id: queueId, // Retorna el ID si no se encuentra en el backend
                name: queueId, // Usa el ID como nombre si no hay datos
                attendedCalls: 0,
                abandonedCalls: 0,
                attendedPercentage: 0,
                abandonedPercentage: 0,
                avgTalkTime: 0,
                avgWaitTime: 0,
                callsInQueue: 0,
                freeAgents: 0,
              }
            );
          }),
        }));

        // Actualizar estadísticas generales
        setGeneralStats(data.generalStats);

        if (statusLoading) {
          setStatusLoading(false);
        }
      } catch (error) {
        console.error("Error fetching realtime status:", error);
      }
    };

    fetchRealtimeStatus();

    const interval = setInterval(fetchRealtimeStatus, 2000);
    return () => clearInterval(interval);
  }, [loading]);

  const {
    realtimeQueuePermissions,
    realtimeAgentPermissions,
    realtimeAgents,
    realtimeQueues,
  } = permissions;

  return (
    <div
      className={`w-full h-[95vh] flex flex-col p-2 ${
        isFullScreen ? (isDarkMode ? "bg-gray-100" : "bg-gray-900") : ""
      }`}
    >
      {isUnauthorized ? (
        <PermissionsKo />
      ) : (
        <>
          <div className="w-full h-[3%] flex items-center justify-end px-2">
            <button
              className={`flex items-center gap-2 px-2 py-1 ${
                isDarkMode
                  ? "bg-primary-dark text-white"
                  : "bg-white text-gray-700"
              } shadow-md rounded hover:bg-opacity-90 text-sm font-semibold`}
              onClick={handleFullScreen}
            >
              Full Screen
              <MdFullscreen />
            </button>
          </div>

          <div
            ref={contentRef}
            className={`w-full h-[97%] flex flex-col gap-4 ${
              isFullScreen
                ? isDarkMode
                  ? "bg-secondary-dark p-4"
                  : "bg-secondary-light p-4"
                : ""
            }`}
          >
            {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <EuiLoadingLogo logo="visMetric" size="xl" />
              </div>
            ) : (
              <>
                {realtimeQueuePermissions && (
                  <div className="w-full h-[100px] flex-shrink-0">
                    <RealtimeQueuesGeneral
                      stats={generalStats}
                      loading={statusLoading}
                    />
                  </div>
                )}
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div
                    className={`flex ${
                      realtimeAgentPermissions ? "flex-row gap-4" : "flex-col"
                    } h-full`}
                  >
                    {realtimeAgentPermissions && realtimeAgents && (
                      <div className="p- flex-1 h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-thumb-rounded hover:scrollbar-thumb-gray-700">
                        <RealtimeAgents
                          agents={realtimeAgents}
                          loading={statusLoading}
                        />
                      </div>
                    )}
                    {realtimeQueuePermissions && (
                      <div
                        className={`${
                          realtimeAgentPermissions ? "flex-[1.2]" : "flex-[1]"
                        } h-full p-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-thumb-rounded hover:scrollbar-thumb-gray-700`}
                      >
                        <RealtimeQueues
                          queues={realtimeQueues}
                          loading={statusLoading}
                          angentsVisible={realtimeAgentPermissions}
                          isFullScreen={isFullScreen} // Nueva prop
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RealtimeMain;
